import React, { useState } from 'react';
import './SettingsModal.css';

interface SettingsModalProps {
  isOpen: boolean;
  onClose: () => void;
  onUsernameChange: (newUsername: string) => void;
  onPasswordChange: (currentPassword: string, newPassword: string) => void;
  onEmailChange?: (newEmail: string, confirmPassword: string) => void;
  currentUsername: string;
  currentEmail?: string;
  isEmailVerified?: boolean;
  userRole?: string;
}

const SettingsModal: React.FC<SettingsModalProps> = ({
  isOpen,
  onClose,
  onUsernameChange,
  onPasswordChange,
  onEmailChange,
  currentUsername,
  currentEmail,
  isEmailVerified,
  userRole
}) => {
  const [usernameForm, setUsernameForm] = useState({
    newUsername: ''
  });
  
  const [passwordForm, setPasswordForm] = useState({
    currentPassword: '',
    newPassword: '',
    confirmPassword: ''
  });
  
  const [emailForm, setEmailForm] = useState({
    newEmail: '',
    confirmPassword: ''
  });
  
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');

  const handleUsernameSubmit = () => {
    if (!usernameForm.newUsername.trim()) {
      setError('Please enter a new username');
      return;
    }
    
    onUsernameChange(usernameForm.newUsername);
    setSuccess('Username changed successfully!');
    setUsernameForm({ newUsername: '' });
    setTimeout(() => setSuccess(''), 3000);
  };

  const handlePasswordSubmit = () => {
    if (!passwordForm.currentPassword || !passwordForm.newPassword || !passwordForm.confirmPassword) {
      setError('Please fill in all password fields');
      return;
    }
    
    if (passwordForm.newPassword !== passwordForm.confirmPassword) {
      setError('New passwords do not match');
      return;
    }
    
    if (passwordForm.newPassword.length < 6) {
      setError('New password must be at least 6 characters');
      return;
    }
    
    onPasswordChange(passwordForm.currentPassword, passwordForm.newPassword);
    setSuccess('Password changed successfully!');
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setTimeout(() => setSuccess(''), 3000);
  };

  const handleEmailSubmit = () => {
    if (!emailForm.newEmail.trim()) {
      setError('Please enter a new email address');
      return;
    }
    
    if (!emailForm.confirmPassword) {
      setError('Please confirm your password to change email');
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(emailForm.newEmail)) {
      setError('Please enter a valid email address');
      return;
    }
    
    if (emailForm.newEmail === currentEmail) {
      setError('New email must be different from current email');
      return;
    }
    
    // This will be handled by the parent component
    if (onEmailChange) {
      onEmailChange(emailForm.newEmail, emailForm.confirmPassword);
      setSuccess('Email change initiated! Please check your new email for verification.');
      setEmailForm({ newEmail: '', confirmPassword: '' });
      setTimeout(() => setSuccess(''), 5000);
    } else {
      setError('Email change functionality is not available');
    }
  };

  const handleClose = () => {
    setError('');
    setSuccess('');
    setUsernameForm({ newUsername: '' });
    setPasswordForm({ currentPassword: '', newPassword: '', confirmPassword: '' });
    setEmailForm({ newEmail: '', confirmPassword: '' });
    onClose();
  };

  if (!isOpen) return null;

  return (
    <div className="settings-modal-overlay" onClick={handleClose}>
      <div className="settings-modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="settings-modal-header">
          <h5>Security Settings</h5>
          <button type="button" className="settings-close-btn" onClick={handleClose}>
            &times;
          </button>
        </div>
        
        <div className="settings-modal-body">
          {/* Account Information Section */}
          <div className="settings-section">
            <h6>Account Information</h6>
            <div className="account-info">
              <div className="info-row">
                <span className="info-label">Email:</span>
                <span className="info-value">{currentEmail}</span>
                {isEmailVerified ? (
                  <span className="status-badge verified">✅ Verified</span>
                ) : (
                  <span className="status-badge unverified">⚠️ Unverified</span>
                )}
              </div>
              <div className="info-row">
                <span className="info-label">Role:</span>
                <span className="info-value">{userRole}</span>
              </div>
              <div className="info-row">
                <span className="info-label">Current Username:</span>
                <span className="info-value">{currentUsername}</span>
              </div>
            </div>
            <div className="security-note">
              <p><strong>Security Note:</strong> Your email address and username cannot be changed for security reasons. Contact your administrator (MASTER user) if you need to change your email or username.</p>
            </div>
          </div>
          
          <hr />
          
          <div className="settings-section">
            <h6>Change Username</h6>
            <div className="form-group">
              <label className="form-label">Current Username</label>
              <input
                type="text"
                className="form-control"
                value={currentUsername}
                readOnly
              />
            </div>
            {userRole === 'MASTER' ? (
              <>
                <div className="form-group">
                  <label className="form-label">New Username</label>
                  <input
                    type="text"
                    className="form-control"
                    value={usernameForm.newUsername}
                    onChange={(e) => setUsernameForm({ newUsername: e.target.value })}
                    placeholder="Enter new username"
                  />
                </div>
                <button className="btn btn-outline-primary btn-sm" onClick={handleUsernameSubmit}>
                  Change Username
                </button>
              </>
            ) : (
              <div className="restricted-action">
                <div className="restriction-message">
                  <span className="restriction-icon">🔒</span>
                  <p><strong>Username changes are restricted to MASTER users only.</strong></p>
                  <p>Contact your administrator if you need to change your username.</p>
                </div>
              </div>
            )}
          </div>
          
          <hr />
          
          {/* Email Change Section - MASTER Only */}
          {userRole === 'MASTER' && (
            <>
              <div className="settings-section">
                <h6>Change Email Address</h6>
                <div className="form-group">
                  <label className="form-label">Current Email</label>
                  <input
                    type="email"
                    className="form-control"
                    value={currentEmail || ''}
                    readOnly
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">New Email Address</label>
                  <input
                    type="email"
                    className="form-control"
                    value={emailForm.newEmail}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, newEmail: e.target.value }))}
                    placeholder="Enter new email address"
                  />
                </div>
                <div className="form-group">
                  <label className="form-label">Confirm Password</label>
                  <input
                    type="password"
                    className="form-control"
                    value={emailForm.confirmPassword}
                    onChange={(e) => setEmailForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                    placeholder="Enter your current password"
                  />
                </div>
                <button className="btn btn-outline-warning btn-sm" onClick={handleEmailSubmit}>
                  Change Email Address
                </button>
                <div className="email-change-warning">
                  <p><strong>⚠️ Important:</strong> Changing your email will require verification. You'll receive a confirmation email at the new address.</p>
                </div>
              </div>
              
              <hr />
            </>
          )}
          
          <div className="settings-section">
            <h6>Change Password</h6>
            <div className="form-group">
              <label className="form-label">Current Password</label>
              <input
                type="password"
                className="form-control"
                value={passwordForm.currentPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, currentPassword: e.target.value }))}
                placeholder="Enter current password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">New Password</label>
              <input
                type="password"
                className="form-control"
                value={passwordForm.newPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, newPassword: e.target.value }))}
                placeholder="Enter new password"
              />
            </div>
            <div className="form-group">
              <label className="form-label">Confirm New Password</label>
              <input
                type="password"
                className="form-control"
                value={passwordForm.confirmPassword}
                onChange={(e) => setPasswordForm(prev => ({ ...prev, confirmPassword: e.target.value }))}
                placeholder="Confirm new password"
              />
            </div>
            <button className="btn btn-outline-primary btn-sm" onClick={handlePasswordSubmit}>
              Change Password
            </button>
          </div>
          
          {error && (
            <div className="alert alert-danger mt-3">
              {error}
            </div>
          )}
          
          {success && (
            <div className="alert alert-success mt-3">
              {success}
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default SettingsModal;

